import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, Database, Server, Activity, Globe, RefreshCw } from "lucide-react";

export default function AdminSystemTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [systemHealth, setSystemHealth] = useState(null);

  useEffect(() => { loadSystemHealth(); }, []);

  const loadSystemHealth = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/system/health`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setSystemHealth(data);
    } catch (error) { console.error("System health error:", error); }
  };

  if (!systemHealth) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-system-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
        <Button variant="outline" onClick={loadSystemHealth}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>
      <Card className={`p-6 ${systemHealth.status === "operational" ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
        <div className="flex items-center gap-4">
          {systemHealth.status === "operational" ? <CheckCircle className="w-12 h-12 text-green-600" /> : <AlertTriangle className="w-12 h-12 text-yellow-600" />}
          <div><h3 className="text-xl font-bold capitalize">{systemHealth.status}</h3><p className="text-gray-600">All systems are running normally</p></div>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4"><Database className="w-6 h-6 text-violet-600" /><h3 className="font-semibold">Database</h3><Badge className={systemHealth.database.status === "healthy" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>{systemHealth.database.status}</Badge></div>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(systemHealth.database.collections).map(([name, count]) => (
              <div key={name} className="p-3 bg-gray-50 rounded-lg"><p className="text-2xl font-bold text-gray-900">{count}</p><p className="text-sm text-gray-500 capitalize">{name}</p></div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4"><Server className="w-6 h-6 text-blue-600" /><h3 className="font-semibold">Server</h3></div>
          <div className="space-y-3">
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">Uptime</span><span className="font-medium text-green-600">{systemHealth.server.uptime}</span></div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">Response Time</span><span className="font-medium">{systemHealth.server.response_time}</span></div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg"><span className="text-gray-600">API Version</span><span className="font-medium">{systemHealth.server.api_version}</span></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4"><Activity className="w-6 h-6 text-orange-600" /><h3 className="font-semibold">Today's Activity</h3></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center"><p className="text-3xl font-bold text-blue-600">{systemHealth.today_activity.new_users}</p><p className="text-sm text-gray-600">New Users</p></div>
            <div className="p-4 bg-orange-50 rounded-lg text-center"><p className="text-3xl font-bold text-orange-600">{systemHealth.today_activity.new_contacts}</p><p className="text-sm text-gray-600">New Messages</p></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4"><Globe className="w-6 h-6 text-green-600" /><h3 className="font-semibold">Integrations</h3></div>
          <div className="space-y-2">
            {Object.entries(systemHealth.integrations).map(([name, status]) => (
              <div key={name} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                <span className="capitalize">{name.replace("_", " ")}</span>
                <Badge className={status === "active" ? "bg-green-100 text-green-700" : status === "pending_approval" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}>{status.replace("_", " ")}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
